import { IFilter, IPage } from "@/api/api";
import { getInvoiceById, IInvoice } from "@/api/invoice";
import { deleteCurrentInvoice, sendCurrentInvoice } from "@/api/store";
import { useAuth } from "@/components/context/useAuth";
import { InvoiceDeleteIcon } from "@/components/invoices/InvoiceDeleteIcon";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

interface Props {
    invoice: IInvoice | null;
    currentStoreId: number;
    open: boolean;
    setOpen: (open: boolean) => void;
    setSelectedInvoice: (invoice: IInvoice | null) => void;
    invoiceTableQueryKey: (
        | string
        | number
        | IFilter
        | {
              page: number;
              pageSize: number;
          }
    )[];
}

interface MutationVars {
    storeId: number;
    invoice: IInvoice | null;
}

export const InvoiceDetailModal = ({
    invoice,
    currentStoreId,
    open,
    setOpen,
    setSelectedInvoice,
    invoiceTableQueryKey,
}: Props) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const {
        isLoading,
        isFetching,
        isPending,
        data: selectedInvoice,
    } = useQuery<IInvoice>({
        queryKey: ["selected-invoice"],
        queryFn: () => {
            if (invoice) {
                return getInvoiceById(invoice.id);
            } else throw Error("No invoice item");
        },
        enabled: !!invoice,
    });

    const { mutate: deleteInvoiceMutation } = useMutation({
        mutationFn: ({ storeId }: MutationVars) => {
            return deleteCurrentInvoice(Number(storeId));
        },

        onMutate: async ({ invoice }: MutationVars) => {
            await queryClient.cancelQueries({ queryKey: invoiceTableQueryKey });
            const previous = queryClient.getQueryData(invoiceTableQueryKey);
            queryClient.setQueryData(
                invoiceTableQueryKey,
                (old: IPage<IInvoice>) => {
                    if (old?.items) {
                        // Handle paginated response
                        return {
                            ...old,
                            items: old.items.filter(
                                (inv: IInvoice) => inv.id !== invoice?.id
                            ),
                        };
                    }
                    return old;
                }
            );
            // Return rollback context
            return { previous };
        },
        onError: (_, _vars, context) => {
            // Rollback on failure
            if (context?.previous) {
                queryClient.setQueryData(
                    invoiceTableQueryKey,
                    context.previous
                );
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: invoiceTableQueryKey });
        },
    });

    const { mutate: sendInvoiceMutation } = useMutation({
        mutationFn: ({ storeId }: MutationVars) => {
            return sendCurrentInvoice(Number(storeId));
        },

        onMutate: async ({ invoice }: MutationVars) => {
            await queryClient.cancelQueries({ queryKey: invoiceTableQueryKey });

            // Snapshot previous list of invoices
            const previous = queryClient.getQueryData(invoiceTableQueryKey);

            // Optimistically update the list
            queryClient.setQueryData(
                invoiceTableQueryKey,
                (old: IPage<IInvoice>) => {
                    if (old?.items) {
                        // Handle paginated response
                        return {
                            ...old,
                            items: old.items.map((inv: IInvoice) =>
                                inv.id === invoice?.id
                                    ? { ...inv, status: "open" }
                                    : inv
                            ),
                        };
                    }
                    return old;
                }
            );

            // Return rollback context
            return { previous };
        },
        onError: (_, _vars, context) => {
            // Rollback on failure
            if (context?.previous) {
                queryClient.setQueryData(
                    invoiceTableQueryKey,
                    context.previous
                );
            }
        },
        onSettled: () => {
            // Always refetch to sync with server
            queryClient.invalidateQueries({ queryKey: invoiceTableQueryKey });
        },
    });

    if (!selectedInvoice) return null;
    if (isPending || isLoading || isFetching) {
        return (
            <Dialog
                fullWidth
                maxWidth="sm"
                open={open}
                onClose={() => setOpen(false)}
            >
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        );
    }
    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={() => {
                setSelectedInvoice(null);
                setOpen(false);
            }}
        >
            <DialogTitle sx={{ p: 0 }}>
                <Toolbar variant="dense" sx={{ background: "#f5f5f5" }}>
                    <Typography variant="h6" fontWeight={700}>
                        Receipt
                    </Typography>
                    <Box flexGrow={1} />
                    <IconButton
                        edge="end"
                        onClick={() => {
                            setSelectedInvoice(null);
                            setOpen(false);
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </DialogTitle>
            <DialogContent sx={{ background: "#fffbea", p: 3 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography variant="subtitle2" color="text.secondary">
                        Invoice #{selectedInvoice.id}
                    </Typography>
                    <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ letterSpacing: 1, mt: 1 }}
                    >
                        {`Remaining: $${(selectedInvoice.amount_remaining / 100).toFixed(2)}`}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Status:{" "}
                        {selectedInvoice.status.charAt(0).toUpperCase() +
                            selectedInvoice.status.slice(1)}
                    </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box>
                    <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ mb: 1 }}
                    >
                        Items
                    </Typography>
                    {selectedInvoice.lines &&
                    selectedInvoice.lines.data &&
                    Array.isArray(selectedInvoice.lines.data) ? (
                        selectedInvoice.lines.data.map((item, idx) => (
                            <Box
                                key={item.id ?? idx}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 1,
                                    borderTop:
                                        selectedInvoice.lines.data.length > 1
                                            ? "1px dashed #e0e0e0"
                                            : "none",
                                }}
                            >
                                <Box>
                                    <Stack
                                        direction={"row"}
                                        gap={0.5}
                                        alignItems={"center"}
                                    >
                                        <Link
                                            to={`/dashboard/stores/${currentStoreId}/orders/${item.metadata.order_id}`}
                                            style={{
                                                textDecoration: "none",
                                                color: "#1976d2",
                                            }}
                                        >
                                            <Typography fontWeight={500}>
                                                {item.description ??
                                                    `Item ${idx + 1}`}
                                            </Typography>
                                        </Link>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {item.metadata.finished
                                                ? null
                                                : "(Draft)"}
                                        </Typography>
                                    </Stack>
                                    <Stack direction={"column"}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Order ID: {item.metadata.order_id}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <Stack direction={"row"} alignItems={"center"}>
                                    <Typography fontWeight={500}>
                                        {item.amount
                                            ? `$${(item.amount / 100).toFixed(2)}`
                                            : ""}
                                    </Typography>
                                    {selectedInvoice?.status === "draft" && (
                                        <InvoiceDeleteIcon
                                            invoice_item_id={item.id}
                                            invoiceTableQueryKey={
                                                invoiceTableQueryKey
                                            }
                                            totalItemsCount={
                                                selectedInvoice.lines.data
                                                    .length
                                            }
                                            currentStoreId={currentStoreId}
                                            invoice={invoice}
                                            onModalClose={() => {
                                                setSelectedInvoice(null);
                                                setOpen(false);
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Box>
                        ))
                    ) : (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 2, my: 2 }}
                        >
                            No line items available.
                        </Typography>
                    )}
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                    }}
                >
                    <Typography fontWeight={600}>Total</Typography>
                    <Typography fontWeight={700}>
                        {`$${(selectedInvoice.amount_remaining / 100).toFixed(2)}`}
                    </Typography>
                </Box>
                {selectedInvoice.status === "draft" && user?.is_admin && (
                    <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setOpen(false);
                                sendInvoiceMutation({
                                    storeId: currentStoreId,
                                    invoice: invoice,
                                });
                            }}
                            id="submit"
                            fullWidth
                        >
                            Send
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setOpen(false);
                                deleteInvoiceMutation({
                                    storeId: currentStoreId,
                                    invoice: selectedInvoice,
                                });
                            }}
                            id="delete"
                            fullWidth
                            sx={{
                                backgroundColor: "#ea4a4a",
                                "&:hover": {
                                    backgroundColor: "#c62828",
                                },
                            }}
                        >
                            Delete
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};
